import { FragmentHoleType } from '@/globals/enums';
import dayjs from 'dayjs';
import Hls from 'hls.js';
import Vue from 'vue';
import { tracer } from './print';
import cloneDeep from 'clone-deep';
/**
 * Calculate the total duration of segments
 * @param {*} segments
 * @returns
 */
function calculateTotalDuration(segments) {
  if (segments.length === 0) {
    return 0;
  }
  const lastSegment = segments[segments.length - 1];

  return lastSegment.start + lastSegment.duration;
}

/**
 * Find the current segment based on the current time
 * @param {*} segments
 * @param {*} currentTime
 * @returns
 */
export function findCurrentSegment(segments, currentTime) {
  for (const segment of segments) {
    if (
      segment.start <= currentTime &&
      currentTime < segment.start + segment.duration
    ) {
      return segment;
    }
  }
  return null;
}

export function checkVideoOffEnded({ timestamp, hole, onVideoEnd }) {
  if (hole && dayjs(timestamp).isAfter(hole?.endUnix)) {
    onVideoEnd({
      videoTime: hole.end + 0.2,
      startTimestamp: dayjs(hole.endUnix).valueOf(),
    });
    return true;
  }
  return false;
}

/**
 * Get the fragment by timestamp
 * @param {*} playlist
 * @param {*} timestamp
 * @returns
 */
export function getFragmentByTimestamp(segments, timestamp) {
  const targetTime = dayjs(timestamp);

  for (const segment of segments) {
    if (!segment.programDateTime) {
      console.warn('Segment programDateTime is not available.');
      return null;
    }

    const segmentStartTime = dayjs(segment.programDateTime);
    const segmentEndTime = segmentStartTime.add(segment.duration, 'second');

    if (
      targetTime.isSame(segmentStartTime) ||
      targetTime.isSame(segmentEndTime) ||
      (targetTime.isAfter(segmentStartTime) &&
        targetTime.isBefore(segmentEndTime))
    ) {
      const videoCurrentTime =
        segment.start + targetTime.diff(segmentStartTime, 'second', true);
      const roundedVideoCurrentTime =
        Math.round(videoCurrentTime * 1000) / 1000;
      return {
        fragment: segment,
        videoCurrentTime: roundedVideoCurrentTime,
      };
    }
  }

  console.warn('No fragment found for the provided timestamp.');
  return null;
}

/**
 * Get the current playback time of the video
 * @param {*} videoElement
 * @returns
 */
function getCurrentPlaybackTime(videoElement) {
  return videoElement.currentTime;
}

/**
 * calculate the current segment's details
 * @param {*} playlist
 * @param {*} videoElement
 * @returns
 */
export function getCurrentSegmentDetails(segments, videoElement) {
  if (segments.length === 0) {
    throw new Error(
      'Provided playlist is a master playlist, not a media playlist.'
    );
  }

  const totalDuration = calculateTotalDuration(segments);
  const currentTime = getCurrentPlaybackTime(videoElement);

  const currentSegment = findCurrentSegment(segments, currentTime);
  return {
    currentTime: currentTime,
    totalDuration: totalDuration,
    currentSegment: currentSegment,
  };
}

/**
 *  pause the player before the gap and skip 1 second
 * @param {*} video Video element
 * @param {*} gaps
 */
export function handleVideoHole(video, gaps, onEvent, meta) {
  const currentTime = video.currentTime;
  for (const gap of gaps) {
    if (currentTime >= gap.start && currentTime < gap.start + 0.5) {
      // if the gap is a codec change, we need to handle it differently
      const gapType = gap.endFrag?.codecChanged
        ? FragmentHoleType.CodecChanged
        : gap.type;

      // handle the gap based on the type
      switch (gapType) {
        case FragmentHoleType.SkipOverlap:
          return meta.device.isSafari
            ? handleSkipOverlap(video, gap, onEvent)
            : Promise.resolve();
        case FragmentHoleType.VideoOff:
          return handleVideoOff(video, gap, onEvent);
        case FragmentHoleType.CodecChanged:
          return handleCPDChange(video, gap, onEvent);
      }

      break;
    }
  }
  return Promise.resolve();
}

function handleSkipOverlap(video, gap, onEvent) {
  return new Promise((resolve, reject) => {
    const currentTime = video.currentTime;
    onEvent({ type: gap.type, isSkipping: true, gap }); // set the flag to true to indicate skipping is in process
    video.pause();
    console.warn(
      `[WARN] Overlap hole detected. Video paused with currentTime: ${currentTime} and the gap is is ${gap.start} with gap duration ${gap.duration}`
    );
    setTimeout(() => {
      const gapDuration = Math.abs(parseInt(gap.duration)) || 1;
      const timeToSeek = gap.start + gapDuration;
      video.currentTime = timeToSeek; // skip 1 second forward
      video
        .play()
        .then(() => {
          onEvent({ type: gap.type, isSkipping: false, gap }); // reset the flag after skipping
          resolve(true);
        })
        .catch((error) => {
          console.error('Error playing video:', error);
          onEvent({ type: gap.type, isSkipping: false, gap }); // reset the flag if an error occurs
          reject(error);
        });
    }, 500); // 0.5 second delay to ensure we are right at the gap
  });
}

function handleVideoOff(video, gap, onEvent) {
  return new Promise((resolve) => {
    console.warn('[WARN] Video off hole detected. Video paused', gap);
    video.pause();
    onEvent({ type: gap.type, isVideoOff: true, gap });
    resolve(true);
  });
}

function handleCPDChange(video, gap, onEvent) {
  return new Promise((resolve) => {
    console.warn('[WARN] CPD change detected. Video paused');
    video.pause();
    onEvent({ type: gap.type, gap });
    resolve(true);
  });
}

/**
 *  Check if backbuffer exists in the given fragments
 * @param {*} fragments
 * @param {*} startTimestamp
 * @returns
 */
export const checkFragExistence = (
  fragments,
  startTimestamp,
  tolerance = 0
) => {
  if (fragments.length === 0) {
    return false;
  }
  const startDayjs = dayjs(startTimestamp).millisecond(0);

  for (let i = 0; i < fragments.length; i++) {
    const fragment = fragments[i];

    const fragmentStartDayjs = dayjs(fragment.programDateTime).millisecond(0);
    const fragmentEndDayjs = fragmentStartDayjs
      .add(fragment.duration, 'seconds')
      .millisecond(0);

    for (let j = 0; j <= tolerance; j++) {
      const currentStartDayjs = startDayjs.add(j, 'second');
      const currentEndDayjs = startDayjs.add(j, 'second');

      if (
        (fragmentStartDayjs.isSame(currentStartDayjs) ||
          fragmentStartDayjs.isBefore(currentStartDayjs)) &&
        (fragmentEndDayjs.isSame(currentEndDayjs) ||
          fragmentEndDayjs.isAfter(currentEndDayjs))
      ) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Get all holes in the fragment list
 * @param {*} playList
 * @returns
 */
export const getAllFragmentHoles = (playList) => {
  if (playList.length === 0) {
    return [];
  }

  const holes = [];
  for (let i = 0; i < playList.length - 1; i++) {
    const currentFragment = playList[i];
    const nextFragment = playList[i + 1];

    if (nextFragment.discontinuity) {
      const gap = dayjs
        .duration(
          dayjs(nextFragment.programDateTime).diff(
            dayjs(currentFragment.programDateTime).add(
              currentFragment.duration,
              'seconds'
            )
          )
        )
        .asSeconds();

      const parsedGapDuration = parseInt(gap);
      const type =
        parsedGapDuration > 0
          ? FragmentHoleType.VideoOff
          : gap < 0
          ? FragmentHoleType.SkipOverlap
          : FragmentHoleType.SkipOrCodecChanged;

      const hole = {
        duration: gap,
        type,
        startFrag: currentFragment,
        endFrag: nextFragment,
        start: currentFragment.start + currentFragment.duration - 0.5,
        end: nextFragment.start,
        startUnix: currentFragment.programDateTime,
        endUnix: nextFragment.programDateTime,
      };

      if (nextFragment.codecChanged) {
        hole.type = FragmentHoleType.CodecChanged;
      }

      holes.push(hole);
      // add the hole information to the next fragment
      Vue.set(nextFragment, 'hole', cloneDeep(hole));
    }
  }
  tracer('getAllFragmentHoles', { holes });
  return holes;
};

export async function hlsParser(playlistUrl, signal) {
  return new Promise((resolve, reject) => {
    if (Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(playlistUrl);

      const abortHandler = () => {
        hls.destroy();
        reject(new DOMException('Aborted', 'AbortError'));
      };

      if (signal) {
        signal.addEventListener('abort', abortHandler);
      }

      hls.on(Hls.Events.LEVEL_LOADED, (event, data) => {
        if (signal) {
          signal.removeEventListener('abort', abortHandler);
        }
        const fragments = data.details.fragments;

        hls.destroy();
        resolve(mapFragments(fragments));
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        if (signal) {
          signal.removeEventListener('abort', abortHandler);
        }
        hls.destroy();
        reject(
          new Error(`Error loading playlist: ${data.type} - ${data.details}`)
        );
      });
    } else {
      reject(new Error('HLS.js is not supported in this browser'));
    }
  });
}

export function mapFragments(fragments) {
  for (let i = 0; i < fragments.length - 1; i++) {
    // set formatted local time for debugging
    fragments[i].localTime = dayjs(fragments[i].programDateTime).format(
      'HH:mm:ss'
    );

    // set discontinuity flag
    fragments[i + 1].discontinuity = fragments[i].cc !== fragments[i + 1].cc;

    // set discontinuity sequence
    fragments[i].discontinuitySequence = fragments[i].cc;

    // set media sequence number
    fragments[i].mediaSequenceNumber = fragments[i].sn;

    // set fetch url
    fragments[i].fetchUrl =
      fragments[i].baseurl.split('getHLSMediaPlaylist.m3u8?')[0] +
      fragments[i].relurl;
  }
  return fragments;
}

/**
 * Check whether user can seek in the buffer
 */
export function checkSeekBuffer(video, startTimestamp, fragments) {
  const currentFragment = getCurrentSegmentDetails(fragments, video);
  const timeInFragment = getFragmentByTimestamp(fragments, startTimestamp);

  const canSeekBuffer =
    timeInFragment?.fragment?.cc === currentFragment?.currentSegment?.cc;
  return {
    timeInFragment,
    currentFragment,
    canSeekBuffer,
  };
}

/*
 * Detect discontinuity tags
 */
export function detectDiscontinuitiesInM3u8(playlist, fetchUrl) {
  const discontinuityFragments = [];
  const lines = playlist.split('\n');

  for (let i = 0; i < lines.length; i++) {
    if (lines[i].startsWith('#EXT-X-DISCONTINUITY')) {
      const fragmentUrl = lines[i + 3]; // assuming the fragment URL follows the tag line + 3
      discontinuityFragments.push({ index: i, url: fetchUrl + fragmentUrl });
    }
  }

  return discontinuityFragments;
}

/*
 * Truncate the playlist at the discontinuity point
 */
export function truncatePlaylistAtDiscontinuity(
  playlist,
  discontinuityFragments
) {
  const lines = playlist.split('\n');

  if (discontinuityFragments.length > 0) {
    const firstDiscontinuityIndex = discontinuityFragments[0].index;

    // keep the lines up to the discontinuity fragment and add #EXT-X-ENDLIST
    const truncatedLines = lines.slice(0, firstDiscontinuityIndex); // +2 to include the fragment
    truncatedLines.push('#EXT-X-ENDLIST');

    return truncatedLines.join('\n');
  }

  return playlist;
}

export function isTimeCloseToLive(timestamp) {
  return dayjs(timestamp).isAfter(dayjs().subtract(1, 'minute'));
}
